import type { SupportApiOperations } from './support-messaging';
import type { IdentityVerificationProfile, IdentityVerificationStartResult } from './identity';
import type { CompanionApiOperations } from './companion';
import type { DirectDebitApiOperations } from './direct-debits';
import type { ExternalAccountsApiOperations } from './external-accounts';
import type { RecurringPaymentApiOperations } from './recurring-payments';
import type { PotsApiOperations } from './pots';
import type { BudgetsApiOperations } from './budgets';
import type {
  SavingsAccountsQuery,
  SavingsVrpMandate,
  SavingsNominatedAccountChoice,
  SavingsAdvancedMaturity,
} from './savings';
import type {
  DataDefinition,
  DataDocument,
  DataRecord,
  RecordFilter,
  RecordPage,
  RecordSort,
} from '../protocol';
import type {
  AppAuthFlowComplete,
  AppAuthFlowField,
  AppAuthFlowMethod,
  AppAuthFlowState,
  AppAuthFlowStep,
  AppAuthLoginRequest,
  AppAuthLoginResult,
  AppAuthLogoutResult,
  AppAuthStatus,
} from './auth';
import type {
  AppCardAuthentication,
  AppCardAuthenticationsQuery,
  CardCreateRequest,
  CardCreateResult,
  CardOrderOptions,
  AppCardLimit,
  AppCardSecureKey,
  AppEncryptedCardDetails,
  AppEncryptedCardPin,
  KeelmoneyCardControls,
  AppCardCancelRequest,
  AppCardReplaceRequest,
  AppCardReplaceResult,
  AppCardLimitsUpdate,
  AppWalletBeginProvisioningRequest,
  AppWalletCompleteProvisioningRequest,
  AppWalletCompleteProvisioningResult,
  AppWalletProvisioningPayload,
} from './cards';
import type { AppAlertCatalogue } from './alerts';
import type { AppPostalAddress } from './beneficiaries';
import type {
  AppAddressSuggestion,
  AppCompanyDetails,
  AppCompanySummary,
  AppResolvedAddress,
} from './company';
import type {
  AppDocumentCategory,
  AppDocumentContext,
  AppDocumentDownloadResult,
  AppDocumentRequest,
  AppDocumentUploadRequest,
  AppStoredDocument,
} from './documents';
import type {
  ExchangeTransactionSummary,
  ExchangeTransactionDetail,
  ExchangeAcceptResult,
  ExchangeQuoteRequest,
  ExchangeQuoteResult,
} from './exchange';
import type { AppHandoffRecord } from './handoffs';
import type {
  DeviceTrustApiOperations,
  AppDeviceEnrolRequest,
  AppDeviceEnrolResult,
  AppBiometricCredential,
  AppBiometricRevokeResult,
  AppDeviceListResult,
  AppDeviceRemoveResult,
} from './device';
import type {
  AppNotificationSettings,
  AppNotificationSettingsPatch,
  AppNotificationSettingsUpdateResult,
  AppNotificationPreferences,
  AppNotificationsMarkReadResult,
  AppNotificationsPage,
  AppPushRegisterRequest,
  AppPushRegisterResult,
  AppPushUnregisterRequest,
  AppPushUnregisterResult,
} from './notifications';
import type {
  CustomerMatchRequest,
  CustomerMatchResult,
  OnboardBusinessRequest,
  OnboardBusinessResult,
  OnboardIndividualRequest,
  OnboardIndividualResult,
  AppOnboardingCheckStatus,
  AppOnboardingJourney,
  AppOnboardingIdentityCreateRequest,
  AppOnboardingIdentityCreateResult,
  AppOnboardingFlow,
  AppOnboardingKind,
  AppOnboardingRequirements,
  AppOnboardingStatus,
  AppOnboardingStepId,
  AppVerificationConfirmRequest,
  AppVerificationConfirmResult,
  AppVerificationSendRequest,
  AppVerificationSendResult,
  AppVerifyIdentityRequest,
  AppVerifyIdentityResult,
} from './onboarding';
import type {
  CopCheckRequest,
  CopCheckResult,
  InternationalPaymentPrecheckResult,
  InternationalPaymentRequest,
  InternationalPaymentResult,
  InternalTransferRequest,
  InternalTransferResult,
  PaymentInitiationRequest,
  PaymentInitiationResult,
  PaymentRequestCreateRequest,
  PaymentRequestCreateResult,
  PaymentRequestDecisionRequest,
  PaymentRequestDecisionResult,
  AppBulkPayment,
  AppBulkPaymentDetail,
  AppPaymentRequest,
  AppPaymentScheme,
  AppScheduledPayment,
  ScheduledPaymentUpdate,
  ScheduledPaymentUpdateResult,
  AppTransactionDetail,
  AppTransferReason,
} from './payments';
import type {
  BeneficiaryCreateRequest,
  BeneficiaryCreateResult,
  BeneficiaryValidateResult,
  InternationalBeneficiaryCreateRequest,
  AppBeneficiaryRequirements,
} from './beneficiaries';
import type {
  NominatedAccountConnectResult,
  NominatedAccountCopDecision,
  NominatedAccountProcessResult,
  SavingsAccountCloseRequest,
  SavingsAccountCloseResult,
  SavingsAccountCreateRequest,
  SavingsAccountCreateResult,
  SavingsAccountRenameRequest,
  SavingsAccountRenameResult,
  SavingsInternalTransferRequest,
  SavingsInternalTransferResult,
  SavingsMaturitySetRequest,
  SavingsMaturitySetResult,
  SavingsWithdrawRequest,
  SavingsWithdrawResult,
  AppNominatedAccount,
  AppNominatedAccountInstitution,
  AppSavingsAccountRequest,
  PispDepositPrepareRequest,
  PispDepositPrepareResult,
} from './savings';

export interface AppAccount {
  accountId: string;
  name: string;
  /** Customer display alias ("GBP Account 1"); `name` is unchanged. Absent when the account has none. */
  alias?: string;
  currency: string;
  availableBalance: number;
  /** First-currency compatibility fields above never sum balances across currencies. */
  accountType?: 'current' | 'multi-currency' | 'savings';
  currencies?: string[];
  balances?: Array<{
    currency: string;
    balance?: number;
    availableBalance?: number;
    indicativeBalance?: number;
    ledgerBalance?: number;
    ledgerBalanceIncludingPendingExchange?: number;
  }>;
  identifiers?: Array<{
    paymentSchemes?: Array<{
      scheme: 'chaps' | 'sepa' | 'faster-payments' | 'swift' | 'ach' | 'wire';
      currency: string;
      country?: string;
      accountNumber?: string;
      iban?: string;
      bankName?: string;
      bankAddress?: string;
      sortCode?: string;
      bic?: string;
      aba?: string;
    }>;
    accountNumber?: string;
    sortCode?: string;
    iban?: string;
    bic?: string;
    ledgerNumber?: string;
    aba?: string;
    ach?: string;
    bsbCode?: string;
    ifsc?: string;
    rtnCanada?: string;
    clabe?: string;
    bankCode?: string;
    branchCode?: string;
  }>;
  parentAccountId?: string;
  status?: string;
  accountNumber?: string;
  sortCode?: string;
  iban?: string;
}

/** Capabilities of this project's configured account provider. */
export interface AccountProvisioningCapabilities {
  provider: string;
  supportsAccountV2: boolean;
  supportedRelationshipTypes: Array<'standalone' | 'parent' | 'child'>;
  supportedAccountModels: Array<'single-currency' | 'multi-currency'>;
  supportedOwnerTypes: Array<'organisation' | 'person'>;
  requiresCurrencies: boolean;
  requiresPrimaryCurrency: boolean;
  requiresPersonIdForPersonOwner: boolean;
  supportsRequestedCapabilities: {
    payments: boolean;
    cards: boolean;
    virtualCards: boolean;
    physicalCards: boolean;
  };
  notes?: string[];
}

export type AccountCreateRequest =
  | { currency: string; name?: string }
  | {
      name: string;
      accountModel: 'single-currency' | 'multi-currency';
      relationshipType: 'standalone' | 'parent' | 'child';
      parentAccountId?: string;
      currencies: string[];
      primaryCurrency?: string;
      capabilities?: { payments?: boolean; cards?: boolean; virtualCards?: boolean; physicalCards?: boolean };
    };

export interface AppBeneficiary {
  /** Customer display alias; name remains the legal account-holder name. Absent/empty means no alias. */
  nickname?: string;
  favourite?: boolean;
  beneficiaryId: string;
  name: string;
  accountNumber?: string;
  sortCode?: string;
  iban?: string;
  currency?: string;
}

/** Maximum decoded profile picture size: 1 MiB. */
export const PROFILE_PICTURE_MAX_BYTES = 1024 * 1024;

export interface AppProfilePicture {
  dataUrl: string;
}

export interface AppProfilePictureUpload {
  contentType: 'image/jpeg' | 'image/png' | 'image/webp';
  /** Base64 image bytes, without a data URL prefix. */
  contentBase64: string;
}

/**
 * The signed-in customer as the platform holds them — the read half of
 * `profile.update`, and deliberately the same field names the write half accepts so a
 * form can round-trip one shape instead of translating between two.
 *
 * Everything but `id` is optional because the sources are optional: the demographic
 * fields come from Keycloak, `kyc` and `applicationReference` from
 * org-metadata-service, and a host that cannot reach either still answers the topic
 * with the session's own id rather than failing it. Absence therefore means "not
 * known here", never "empty".
 */
export interface AppProfile {
  id: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  /** Whether the platform holds a verified email — the claim the OTP gate establishes. */
  emailVerified?: boolean;
  /** E.164, including the country code. */
  phone?: string;
  phoneVerified?: boolean;
  /** ISO-8601 date, `YYYY-MM-DD`. */
  dob?: string;
  /**
   * The customer's registered address. The field names match
   * `ProfileUpdateRequest['address']` exactly — a read that answered `line1` and a write
   * that took `addressLine1` would be one profile with two vocabularies.
   */
  address?: {
    /** ISO-3166 alpha-2. */
    country?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    addressLine4?: string;
    city?: string;
    county?: string;
    postalCode?: string;
  };
  marketingPreferences?: {
    email?: boolean;
    sms?: boolean;
    push?: boolean;
    mail?: boolean;
  };
  /** The organisation's application reference, zero-padded, where one has been issued. */
  applicationReference?: string;
  /**
   * ISO timestamp before which a further profile change is refused, when the project
   * runs a contact-change cooldown. Absent when the project runs none.
   */
  profileUpdatePermittedDate?: string;
  /**
   * The customer's identity check, in the same vocabulary the `onboardingStatus` topic
   * uses — an unrecognised provider value normalises to 'pending', never to a terminal
   * answer. Absent when nothing has been recorded.
   */
  kyc?: AppOnboardingCheckStatus;
  activeRole?: string;
}

/**
 * The signed-in customer's active organisation as stored at onboarding (`organisation.get`).
 * Everything but the id and name is optional: a personal customer's organisation usually
 * holds no registration details or business address.
 */
export interface AppOrganisation {
  organisationId: string;
  name: string;
  /** Company registration number given at business onboarding. */
  identificationNumber?: string;
  incorporationDate?: string;
  /** Registered business address; `state` is the county. Absent when none was stored. */
  address?: AppPostalAddress;
}

/** One selectable option from a platform-held reference list (`lookups.list`). */
export interface AppLookupOption {
  value: string;
  label: string;
  /** Provider reference data such as industry grouping and classification code. */
  metadata?: Record<string, unknown>;
}

/**
 * The reference lists the platform serves. Several are provider-backed (the savings
 * catalogues are the project's own products); the rest are platform-held.
 * Shared by the SDK's discoverable catalogue and both host allowlists.
 */
export const APP_LOOKUP_TYPES = [
  'titles',
  'source-of-funds',
  'account-purpose',
  'countries',
  'no-tin-reasons',
  'savings-products',
  'savings-products-renewal',
  'industry-categories',
] as const;
export type AppLookupType = (typeof APP_LOOKUP_TYPES)[number];

/**
 * The signed-in customer editing their own details. Every field is optional and only the
 * fields present are written — this is a patch, not a replacement, and omitting a field
 * leaves it alone rather than clearing it.
 *
 * **Changing `email` or `phone` requires a live OTP verification of the new value.** Run
 * `onboarding.verify.send` then `onboarding.verify.confirm` for that channel and value first;
 * without one the call is refused with `verification-required` rather than silently writing an
 * unverified contact. Verification is bound to the value and expires, so verifying a@b.com
 * does not license writing c@d.com. The other fields need no verification.
 *
 * A project may narrow what a customer may edit at all (`permittedProfileUpdateFields`) and
 * may impose a cooldown between contact changes; both are refusals from the platform, not
 * something this contract can predict. Render the refusal, do not try to pre-empt it.
 */
export interface ProfileUpdateRequest {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  /** ISO-8601 date, `YYYY-MM-DD`. */
  dob?: string;
  email?: string;
  /** E.164, including the country code — the platform validates it as a phone number. */
  phone?: string;
  address?: {
    /** ISO-3166 alpha-2. */
    country?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    addressLine4?: string;
    city?: string;
    county?: string;
    postalCode?: string;
  };
  /** Absent channels keep their current setting; a present one is written as given. */
  marketingPreferences?: {
    email?: boolean;
    sms?: boolean;
    push?: boolean;
    mail?: boolean;
  };
}

export interface AppTransaction {
  transactionId: string;
  amount: number;
  currency: string;
  description?: string;
  date?: string;
  type?: string;
  /** Provider-reported lifecycle status; absent when the provider does not supply it. */
  status?: string;
  /**
   * The account balance after this transaction, as the provider reports it.
   * Absent when the provider does not report one.
   */
  balance?: number;
}

export interface AppCard {
  cardId: string;
  name?: string;
  /** Provider-masked PAN (e.g. '**** **** **** 4242'). Full PAN/CVV/PIN are never exposed to widgets. */
  maskedPan?: string;
  status: string;
  /** e.g. 'physical' | 'virtual' */
  type?: string;
  currency?: string;
}

export interface AppSavingsAccount {
  /** Literature for this account's original product issue, including withdrawn products. */
  documents?: AppSavingsProductDocuments;
  savingsAccountId: string;
  productName?: string;
  /** e.g. 'easy-access' | 'fixed-term' | 'notice' */
  accountType?: string;
  /** Provider notice period before funds become accessible. */
  noticePeriod?: { value: number; type: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR' };
  currency: string;
  balance: number;
  /**
   * What can actually leave the pot right now: balance minus anything the
   * provider holds back (accrued fixed-term interest, a pending notice
   * withdrawal, …). The native withdrawal flow validates the amount against
   * this, never against `balance` — a withdraw widget should too.
   */
  availableBalance?: number;
  /** Percentage, e.g. 4.5 for 4.5% */
  interestRate?: number;
  startDate?: string;
  endDate?: string;
  status?: string;
  /*
   * The four capability flags below are published by the provider per account
   * and lifecycle state (a fixed-term pot stops being fundable when its funding
   * window closes, becomes withdrawable again inside its maturity window, …).
   * Absence means the provider did not say: the platform's own guards only
   * block on an explicit `false`, so treat absence as "not blocked", not "off".
   */
  /**
   * Whether the pot can currently take money in — deposits, and the receiving
   * side of `savings.internalTransfer` (the platform rejects a transfer into a
   * pot with this explicitly false).
   */
  isFundingEnabled?: boolean;
  /**
   * Whether money can currently leave — `savings.withdraw`, and the sending
   * side of `savings.internalTransfer`.
   */
  isWithdrawalEnabled?: boolean;
  /** Whether the account can be closed (`savings.close`). */
  isClosureEnabled?: boolean;
  /**
   * Whether a maturity decision is currently actionable
   * (`savings.maturity.set`): true for a fixed-term account inside its
   * maturity window, false outside it and always for easy-access/notice.
   */
  isRenewalEnabled?: boolean;
  /*
   * Everything below is published per account by the savings providers and was
   * dropped by the BFF mapper until 2026-08-29. All optional, because which of
   * them a provider fills is a provider fact: thisbank fills the term/maturity
   * projections, universal-sandbox fills accrued interest, the routing
   * identifiers and the withdrawal allowance, and neither fills all of them.
   * Absence means "this provider did not say", never zero.
   */
  /** The product this pot was opened on — the key into `savings.products.list`. */
  productCode?: string;
  /** The customer's own nickname for the pot (`savings.accounts.rename` writes it). */
  accountName?: string;
  /** ISO-3166 alpha-2. */
  country?: string;
  /*
   * The pot's own routing identifiers, whichever the provider actually
   * publishes — a UK pot carries accountNumber/sortCode, a SEPA one iban/bic.
   * Read them as optional and render what is present rather than assuming a
   * scheme, exactly as for `AppNominatedAccount`. These identify the pot for
   * an inbound deposit; money never leaves a pot to an arbitrary destination.
   */
  accountNumber?: string;
  sortCode?: string;
  iban?: string;
  bic?: string;
  /** Interest earned but not yet paid, in the account's currency. */
  interestAccrued?: number;
  /** Fixed-term only: interest the pot is projected to have earned at term end. */
  interestAtMaturity?: number;
  /** Fixed-term only: the projected closing balance at term end. */
  balanceAtMaturity?: number;
  /** Annual equivalent rate percentage, when the provider publishes one for the account. */
  aer?: number;
  termMonths?: number;
  issueNumber?: number;
  /** Total paid in since opening, in the account's currency. */
  deposits?: number;
  /** Provider cap on the total that may be held in this pot. */
  maxDeposit?: number;
  /** Provider floor for this pot. */
  minBalance?: number;
  /**
   * Last permitted funding date (ISO date/time), not the date of the latest deposit.
   * A moving deadline on a fixed-term pot: until the first deposit it is the last day
   * for that first deposit; once funded it becomes the funding window end.
   */
  lastFundingDate?: string;
  /**
   * Fixed-term only: when the maturity window opens, i.e. the first moment
   * `savings.maturity.set` is actionable. `isRenewalEnabled` is the same fact as
   * a boolean measured now; this is the date to render before then.
   */
  maturityWindowOpensAt?: string;
  /**
   * The provider's own account metadata (e.g. `saascada`), forwarded as-is so it
   * can be inspected while debugging. Its shape is the provider's, not the
   * platform's, and may change without notice — do not build on it; every fact
   * an app should rely on is a typed field above.
   */
  metadata?: Record<string, unknown>;
  /**
   * Present only on a *limited-access* product — a pot whose terms allow a fixed
   * number of withdrawals per period rather than an unlimited number. Absence
   * means the pot is not limited-access, NOT that the allowance is unknown: an
   * easy-access pot with no allowance block has no withdrawal count to show.
   *
   * `limit` is the only field a provider is guaranteed to publish (thisbank
   * derives it from the product's terms and knows nothing about consumption);
   * `used`/`remaining`/`resetsAt` come from a provider that counts withdrawals
   * per period, so a widget that renders "2 of 3 left" must handle their absence.
   */
  withdrawalAllowance?: AppSavingsWithdrawalAllowance;
}

/**
 * A limited-access pot's withdrawal allowance.
 *
 * `period` is the provider's own period name (universal-sandbox publishes e.g.
 * `CALENDAR_YEAR`, `ROLLING_12M`) and is deliberately not narrowed to a union:
 * the set is a provider's product configuration, not a platform enum, and a
 * widget that switches on an unexpected value would be worse than one that
 * renders the label it was given.
 */
export interface AppSavingsWithdrawalAllowance {
  /** Withdrawals permitted in one period. */
  limit: number;
  /** Withdrawals already counted against `limit` this period. */
  used?: number;
  /** `limit - used`, as the provider counts it. */
  remaining?: number;
  period?: string;
  /** ISO timestamp at which `used` returns to zero. */
  resetsAt?: string;
}

export interface AppSavingsProduct {
  productCode: string;
  productName: string;
  /** e.g. 'easy-access' | 'fixed-term' | 'notice' */
  accountType?: string;
  currency?: string;
  /** Gross rate percentage. */
  interestRate?: number;
  /** Annual equivalent rate percentage, when published. */
  aer?: number;
  termMonths?: number;
  /** Minimum balance / opening deposit for the product. */
  minBalance?: number;
  maxDeposit?: number;
  /** The provider's own prose description of the product. */
  description?: string;
  /** ISO date after which the product can no longer be opened. */
  expiry?: string;
  /**
   * Limited-access products only: how many withdrawals the terms allow per year.
   * The product-level statement of the same rule an open account reports live as
   * `AppSavingsAccount.withdrawalAllowance` — this is what a product picker
   * shows before there is an account to count against.
   */
  maxWithdrawalsPerYear?: number;
  /**
   * The product literature a customer has to be shown before opening. Absolute
   * URLs to the provider's own documents; open them, do not proxy them. Whichever
   * of the three a provider publishes is what is present.
   */
  documents?: AppSavingsProductDocuments;
}

export interface AppSavingsProductDocuments {
  keyProductInformationUrl?: string;
  termsAndConditionsUrl?: string;
  interestRateTermsUrl?: string;
}

/**
 * Key/value selections made by the surrounding page (host-set state, e.g. a
 * row picked in a native listing widget). There is no fetch behind this topic.
 */
export type AppPageContext = Record<string, string>;

/** `error.details` of a 'rate-limited' rejection. */
export interface AppRateLimitedDetails {
  retryAfterSeconds: number;
}

/** `error.details` of an 'onboarding-prerequisite-unmet' rejection. */
export interface AppPrerequisiteUnmetDetails {
  outstanding: AppOnboardingStepId[];
}

/**
 * `error.details` of an 'invalid-payload' rejection.
 *
 * `field` is the dotted path through the platform's *wire* DTO
 * (`destinationIdentifier.currency`), not the widget contract's field name —
 * the validation runs on the mapped request, which is what has to satisfy the
 * platform's rules. `constraints` are the platform's own constraint messages.
 */
export interface AppInvalidPayloadDetails {
  fields: { field: string; constraints: string[] }[];
}

/**
 * Built-in data topics. Hosts may serve additional project-specific topics.
 *
 * **A topic delivers one payload, once.** There is no page parameter in `data.get`, no way
 * for a widget to ask for a second page, and no shape in the protocol for it to arrive in.
 * So a *collection* served as a topic is always a single arbitrary page presented as if it
 * were the whole set — a widget rendering "your transactions" from it is rendering "some of
 * your transactions" and cannot know it. That is a correctness problem, not an ergonomic
 * one, and no amount of host work fixes it while the read mechanism has no pagination.
 *
 * Every collection therefore moved to `api.call`, which does take parameters and can be
 * asked again: `accounts.list`, `transactions.list`, `cards.list`, `beneficiaries.list`,
 * `savings.accounts.list`, `savings.products.list`. What remains below is the set of things
 * that were never a collection fetch — a single object, host-held state, or an instance's
 * own record — and for which "one payload, once" is the correct contract rather than a
 * truncation.
 *
 * `notifications` is not a topic for the same reason: a feed is a collection, and a topic
 * could only ever deliver an arbitrary first page of it. It is `notifications.list`, which
 * takes `page`/`size` and can be asked again, plus the `notifications.received` event as the
 * live channel — the two together are what a bell needs, and neither is a truncation.
 */
export interface AppDataTopics {
  profile: AppProfile;
  /** Host-set selections from the surrounding page. No fetch behind it. */
  pageContext: AppPageContext;
  /** This widget instance's redirect hand-offs, newest last. */
  handoffs: AppHandoffRecord[];
  /** Normalised KYC/KYB + contact verification. Polled, not pushed — see the type. */
  onboardingStatus: AppOnboardingStatus;
  /**
   * The project's configured alert copy, in its default locale. A single object,
   * read once and cached — the copy changes when operators edit it, not per call.
   * It is an OPTIONAL overlay on the typed error family; see contracts/alerts.ts.
   */
  alerts: AppAlertCatalogue;
}

export interface AppPage<T> {
  items: T[];
  page?: number;
  totalItems?: number;
}

/** Built-in api operations. Hosts may expose additional named operations. */
export type SavingsAccountBalanceBreakdown =
  | {
      available: true;
      savingsAccountId: string;
      currency: string;
      principalBalance: number;
      interestPosted: number;
      interestAccrued: number;
      predictedMaturityBalance: number;
      totalInterestAtMaturity: number;
    }
  | { available: false; savingsAccountId: string; reason: string };

export interface AppApiOperations
  extends DeviceTrustApiOperations,
    SupportApiOperations,
    CompanionApiOperations,
    DirectDebitApiOperations,
    ExternalAccountsApiOperations,
    PotsApiOperations,
    BudgetsApiOperations,
    RecurringPaymentApiOperations {
  'connections.connect': { payload: { connection: string }; result: { authorizationUrl: string; expiresAt: string } };
  'connections.status': { payload: { connection: string }; result: { connected: boolean; expiresAt: string | null } };
  'connections.disconnect': { payload: { connection: string }; result: { connected: false } };

  /** Searches the configured company registry; at least one criterion is required. */
  'companies.search': {
    payload: { companyName?: string; location?: string; page?: number; size?: number };
    result: AppPage<AppCompanySummary>;
  };
  /** Returns one company and its available registry metadata, including officers. */
  'companies.get': { payload: { companyNumber: string }; result: AppCompanyDetails };
  /** Returns display suggestions from the project's configured address provider. */
  'addresses.suggest': { payload: { query: string }; result: AppAddressSuggestion[] };
  /** Resolves an opaque suggestion id returned by `addresses.suggest`. */
  'addresses.resolve': { payload: { id: string }; result: AppResolvedAddress };
  /**
   * One of the platform's reference lists, as `{ value, label }` pairs ready for a
   * select. The list is the platform's, not the application's: an application that
   * hard-codes titles or source-of-funds codes sends values the provider rejects,
   * because two of these lists (`savings-products`, `savings-products-renewal`) are the
   * project's own product catalogue and change without the application shipping.
   *
   * An unknown `type` is refused rather than answered with an empty list — an empty
   * select and a misspelt list name must not look the same.
   */
  'lookups.list': { payload: { type: AppLookupType }; result: AppLookupOption[] };
  'payments.initiate': { payload: PaymentInitiationRequest; result: PaymentInitiationResult };
  'accounts.get': { payload: { accountId: string }; result: AppAccount };
  'accounts.create': { payload: AccountCreateRequest; result: AppAccount };
  'accounts.capabilities': { payload: Record<string, never>; result: AccountProvisioningCapabilities };
  'accounts.rename': { payload: { accountId: string; name: string }; result: { updated: boolean } };
  'accounts.close': { payload: { accountId: string }; result: { success: boolean } };
  'accounts.list': { payload: { page?: number; size?: number }; result: AppPage<AppAccount> };
  /**
   * Marks one account as the page's *selected* account — the same app-level
   * selection the native account listings set on a row click, which the
   * platform's balance, transaction and payment widgets read as "the account
   * in view" (and which operations with an optional `accountId` default to).
   * Selection is how a listing widget hands a choice to the native widgets on
   * the pages it navigates to; it never reaches the provider.
   *
   * Unlike `beneficiaries.select`, the native handler refuses an id it has
   * not listed: an unknown accountId resolves but leaves the selection
   * unchanged. Pass an id straight from `accounts.list` / the `accounts`
   * topic. Selecting a different multi-currency account also resets the
   * selected currency to that account's first currency, exactly as the
   * native listing does.
   */
  'accounts.select': { payload: { accountId: string }; result: { selected: true } };
  /**
   * Marks one currency as the page's *selected* currency on the selected
   * multi-currency account — the same app-level selection the native
   * multi-currency widget sets on a currency click, which balance,
   * transaction and payment widgets read alongside the selected account. The
   * native action carries only the code (one currency in the account page
   * state, applying to whichever account is selected), so the payload is just
   * `currency`; it is not validated against the account's held currencies,
   * and nothing reaches the provider. `accounts.select` of a different
   * multi-currency account resets this to that account's first currency.
   */
  'accounts.selectCurrency': { payload: { currency: string }; result: { selected: true } };
  /**
   * `from`/`to` are ISO dates ('YYYY-MM-DD') bounding the transaction date, and
   * `to` must not precede `from`; `sort` orders by transaction date and defaults
   * to the platform's own ordering ('desc'). Omitting all of them keeps the
   * previous behaviour exactly.
   *
   * `direction` lists only credits ('incoming') or debits ('outgoing'), the
   * values AppTransaction.type reports; omitted lists both. The provider does
   * the filtering, exactly as the native /incoming-transactions and
   * /outgoing-transactions listings do, so a provider that does not filter
   * returns both there too.
   */
  'transactions.list': {
    payload: {
      accountId?: string;
      page?: number;
      size?: number;
      from?: string;
      to?: string;
      sort?: 'asc' | 'desc';
      direction?: 'incoming' | 'outgoing';
    };
    result: AppPage<AppTransaction>;
  };
  'beneficiaries.cop.check': { payload: CopCheckRequest; result: CopCheckResult };
  /**
   * accountId defaults to the currently selected account; country to 'GB'.
   * Omitting `category` lists the reason *categories* — the same endpoint serves
   * both levels, so a two-step picker calls this twice.
   */
  'payments.reasons.list': {
    payload: { accountId?: string; country?: string; category?: string };
    result: AppTransferReason[];
  };
  'beneficiaries.requirements': {
    payload: { currency: string; country?: string; scheme?: string };
    result: AppBeneficiaryRequirements;
  };
  /** Confirmation-gated where the project requires it (see docs on SCA). */
  'beneficiaries.create': { payload: BeneficiaryCreateRequest; result: BeneficiaryCreateResult };
  /** Sets only the platform display alias; empty clears it. Idempotent, trims whitespace, at most 255 characters.
   * The legal holder name, bank details and provider ID stay unchanged. Uses updateBeneficiary SCA policy.
   */
  'beneficiaries.updateNickname': {
    payload: { beneficiaryId: string; nickname: string };
    result: { beneficiaryId: string; nickname: string };
  };
  /** Complete editable payee fields; provider-owned row identifiers never enter the contract. Requires a live customer session; scenario previews are refused. */
  'beneficiaries.detail': {
    payload: { beneficiaryId: string };
    result: AppBeneficiary & Omit<InternationalBeneficiaryCreateRequest, 'idempotencyKey'>;
  };
  /** Replaces editable details. Load detail first and submit all fields to retain them. Use the returned beneficiaryId: some providers replace the payee ID. SCA uses updateBeneficiary. */
  'beneficiaries.update': {
    payload: Omit<InternationalBeneficiaryCreateRequest, 'idempotencyKey'> & { beneficiaryId: string };
    result: AppApiOperations['beneficiaries.detail']['result'];
  };
  /** Per-user favourite state; setting the current state again is harmless. */
  'beneficiaries.favourite': {
    payload: { beneficiaryId: string; favourite: boolean };
    result: { success: boolean; alertCodes: string[] };
  };
  'beneficiaries.favourites': {
    payload: AppApiOperations['beneficiaries.list']['payload'];
    result: AppPage<AppBeneficiary>;
  };
  /** Pages outgoing transactions, then resolves payees. Totals describe transactions; pages can be sparse or repeat payees. */
  'beneficiaries.recent': {
    payload: AppApiOperations['beneficiaries.list']['payload'] & { accountId: string };
    result: AppPage<AppBeneficiary & { amount: number; paymentDate: string }>;
  };
  /**
   * The saved beneficiaries, paged and filterable — the one-shot, parameterised
   * counterpart of the `beneficiaries` topic, for building dedicated listing
   * widgets. Reads the same platform-side table as the topic: rows are synced
   * from the provider out-of-band, so a payee created at the provider directly
   * appears here on the platform's sync cadence, not instantly.
   *
   * `name` and `accountNumber` are substring matches (name case-insensitive);
   * `currency` and `country` are exact matches against the destination account.
   * Omitted paging falls back to the platform's defaults (page 1, size 20).
   */
  'beneficiaries.list': {
    payload: {
      page?: number;
      size?: number;
      name?: string;
      currency?: string;
      country?: string;
      accountNumber?: string;
    };
    result: AppPage<AppBeneficiary>;
  };
  /**
   * Marks one saved beneficiary as the page's *selected* beneficiary — the
   * same app-level selection the native beneficiary listing sets, which the
   * platform's payment flows (amount entry, summary, CTA actions) read as
   * "who is being paid". Selection is how a listing widget hands a choice to
   * the native widgets on the pages it navigates to; the selection itself
   * never reaches the provider.
   *
   * The id is not validated against the saved list — selecting an unknown id
   * resolves and leaves downstream pages reading an empty beneficiary, exactly
   * as the native listing behaves. Pass an id straight from
   * `beneficiaries.list` / the `beneficiaries` topic.
   */
  'beneficiaries.select': {
    payload: { beneficiaryId: string };
    result: { selected: true };
  };
  /**
   * Removes one of the customer's saved beneficiaries: the platform row is
   * soft-deleted and the provider's payee removed where the provider keeps one
   * (on Keel, which keeps none, only the platform row goes). Only a beneficiary
   * of the caller's active organisation can be removed; any other id answers
   * not-found. Gated by the native route's `deleteBeneficiary` claim, required
   * only when the project's scaPolicy says so.
   */
  'beneficiaries.delete': {
    payload: { beneficiaryId: string };
    result: { beneficiaryId: string; deleted: true };
  };
  /**
   * The result array is the requested page, not the customer's every card:
   * `totalItems` is not part of this result (the shape predates paging), so page
   * until an answer comes back shorter than `size`. Filters mirror what the
   * native listing accepts — `name` is a substring match, `type` and `status`
   * are exact. Omitting every field keeps today's behaviour byte-for-byte
   * (page 1, size 20).
   */
  'cards.list': {
    payload: {
      page?: number;
      size?: number;
      name?: string;
      type?: 'virtual' | 'physical';
      status?: string;
    };
    result: AppCard[];
  };
  /**
   * Marks one card as the page's *selected* card — the same app-level
   * selection the native cards widget sets on a card click, which the
   * card-management pages (detail, freeze, PIN, limits) read as "the card
   * being managed". Selection is how a listing widget hands a choice to the
   * native widgets on the pages it navigates to.
   *
   * The native dispatch carries the full card record, so the host resolves
   * the id against the cards it has already listed for the page (the `cards`
   * topic's listing): a cardId the host has not listed is an error, not a
   * silent no-op. Selecting also triggers the native follow-up refresh of the
   * card's limits; the selection itself never reaches the provider.
   */
  'cards.select': { payload: { cardId: string }; result: { selected: true } };
  'cards.freeze': { payload: { cardId: string }; result: { status: string } };
  'cards.unfreeze': { payload: { cardId: string }; result: { status: string } };
  /**
   * `type` is the payment direction ('incoming' | 'outgoing' — the value a
   * transaction listing delivered as AppTransaction.type); accountId
   * defaults to the currently selected account.
   */
  'transactions.detail': {
    payload: { transactionId: string; type: string; accountId?: string };
    result: AppTransactionDetail;
  };
  /** Payments still open for cancellation, across the user's accounts. */
  'payments.scheduled.list': {
    payload: { page?: number; size?: number };
    result: AppPage<AppScheduledPayment>;
  };
  /**
   * Future-dated payments waiting to execute — a different listing from
   * `payments.scheduled.list`, which is the cancellation window. This is the
   * native `GET /payments/scheduled` the web front end reads, so a row here is
   * one that has *not* been sent and is due on `scheduledAt`, not one inside a
   * short reversal window.
   *
   * `accountId` filters to one account, and on a provider that holds the
   * schedule itself it is what lets the BFF ask the provider rather than the
   * platform's own job queue — so pass it whenever the screen is about a single
   * account. Cancel a row with `payments.scheduled.cancel`, as for the window
   * listing: both are the same job id.
   */
  'payments.scheduled.upcoming': {
    payload: { accountId?: string; page?: number; size?: number };
    result: AppPage<AppScheduledPayment>;
  };
  'paymentRequests.list': {
    payload: { page?: number; size?: number; status?: string; query?: string };
    result: AppPage<AppPaymentRequest>;
  };
  'paymentRequests.approve': { payload: PaymentRequestDecisionRequest; result: PaymentRequestDecisionResult };
  'paymentRequests.reject': { payload: PaymentRequestDecisionRequest; result: PaymentRequestDecisionResult };
  'paymentRequests.get': { payload: { paymentRequestId: string }; result: AppPaymentRequest };
  'cards.authentications.list': { payload: AppCardAuthenticationsQuery; result: AppPage<AppCardAuthentication> };
  'cards.authentications.get': { payload: { authenticationId: string }; result: AppCardAuthentication };
  'cards.authentications.approve': { payload: { authenticationId: string }; result: AppCardAuthentication };
  'cards.authentications.reject': { payload: { authenticationId: string }; result: AppCardAuthentication };
  'cards.limits.get': { payload: { cardId: string }; result: AppCardLimit[] };
  /**
   * Changes one card's spend limits and answers them as the provider now reports them (the
   * same shape as `cards.limits.get`). Not confirmation-gated; audited. Keel Money and the
   * universal sandbox only — any other card provider is refused with `provider-unsupported`.
   * A non-numeric, negative or over-precise amount is `invalid-payload`.
   */
  'cards.limits.set': { payload: { cardId: string; limits: AppCardLimitsUpdate }; result: AppCardLimit[] };
  /**
   * Activates a card that was issued inactive — a physical card once it arrives, or a
   * replacement physical card. Answers the card as the platform now records it (`cardId`,
   * `status: 'active'`, `type`; re-read `cards.list` for the display fields). Confirmation-gated on
   * `activateCard` (the native route's claim) where the project's SCA policy requires it.
   * Refusals: `provider-unsupported` (the card provider has no activate), a provider 4xx for a
   * card that cannot be activated (already active, cancelled).
   */
  'cards.activate': { payload: { cardId: string }; result: AppCard };
  /**
   * Cancels a card for good — report lost or stolen, or close it. Terminal: the card can
   * never be used or unfrozen again (freeze it instead to pause). `reason` reaches the
   * provider where it has one (see `AppCardCancelReason`). Answers the card as the platform now
   * records it (`status: 'terminated'`). Confirmation-gated on `cancelCard` where the project's
   * SCA policy requires it.
   * Refusals: `provider-unsupported`, `invalid-payload` (no or unknown reason, or a reason the
   * provider refuses — Railsr takes stolen and destroyed only).
   */
  'cards.cancel': { payload: AppCardCancelRequest; result: AppCard };
  /**
   * Replaces a card — lost, stolen, damaged or renewal. The replaced card stops working
   * (except Keel Money, where the card id survives with new card details). See
   * `AppCardReplaceResult` for which card comes back. Not confirmation-gated (the native
   * route is not); audited. Refusals: `provider-unsupported`, `invalid-payload`.
   */
  'cards.replace': { payload: AppCardReplaceRequest; result: AppCardReplaceResult };
  /** The public catalogue: every product the project publishes, unfiltered. */
  /**
   * The savings catalogue.
   *
   * `category` picks *which* catalogue, and the two are genuinely different
   * lists behind one endpoint rather than a filter over one:
   *
   * - 'standard' (the default, and what this operation always sent) — the
   *   new-account catalogue, the products `savings.create` may be called with.
   * - 'renewal' — the renewal catalogue, the products a maturing fixed-term pot
   *   may roll into. These are the only product codes `savings.maturity.set`
   *   accepts for a 'renew' or 'partial-reinvest' decision, and they are NOT in
   *   the standard list. Until 2026-08-29 this operation took no payload, so a
   *   maturity widget had no way to name a product the platform would accept.
   */
  'savings.products.list': {
    payload: {
      category?: 'standard' | 'renewal';
      clientType?: 'personal' | 'corporate';
      accountType?: 'easy-access' | 'fixed-term' | 'notice';
      page?: number;
      size?: number;
    };
    result: AppSavingsProduct[];
  };
  /**
   * The same catalogue narrowed to the signed-in customer's organisation —
   * `savings.products.list` asks "what does this bank sell?", this one asks
   * "what may *this* customer open?". Same `AppSavingsProduct` shape, so a
   * product picker can swap one call for the other; the org is taken from the
   * session by the host and is not a payload field.
   *
   * Build against it, but do not expect it to differ yet: no registered
   * provider implements eligibility today — thisbank, saascada and
   * universal-sandbox all ignore the organisation and answer with the public
   * catalogue, so the two operations return the same array. It is the seam
   * eligibility will arrive through, not a filter that is live.
   */
  'savings.products.listForCustomer': {
    payload: Record<string, never>;
    result: AppSavingsProduct[];
  };
  /**
   * The requested page, defaulting to active accounts, page 1, size 50. Explicit
   * filters may include closed accounts. Use savings.accounts.page for totals.
   */
  'savings.accounts.list': {
    payload: SavingsAccountsQuery;
    result: AppSavingsAccount[];
  };
  /**
   * Marks one savings account as the page's *selected* savings account — the
   * same app-level selection the native savings listings set on a row click,
   * which the savings detail, deposit and withdrawal pages read as "the
   * account in view". Selection is how a listing widget hands a choice to the
   * native widgets on the pages it navigates to; it never reaches the
   * provider.
   *
   * The id is not validated against the listed accounts — selecting an
   * unknown id resolves and leaves downstream pages reading an empty account,
   * exactly as the native listing behaves. Pass an id straight from
   * `savings.accounts.list` / the `savingsAccounts` topic.
   */
  /**
   * On-demand fixed-term breakdown (currently SaaScada only). Monetary values are major units.
   * Principal excludes posted interest; totalInterestAtMaturity is the provider prediction minus principal,
   * including interest already posted. interestAccrued is unposted provider accrual, not added again.
   * Returns available:false when history cannot be reconciled, including unsupported postings.
   * Non-fixed-term accounts are rejected. Existing interestAtMaturity on the account is unchanged.
   */
  'savings.accounts.saascada.balanceBreakdown': {
    payload: { savingsAccountId: string };
    result: SavingsAccountBalanceBreakdown;
  };
  'savings.accounts.get': { payload: { savingsAccountId: string }; result: AppSavingsAccount };
  /** All organisation current and savings accounts from the wealth API, converted to the requested base currency. */
  'savings.balance.total': { payload: { currency: string }; result: { currency: string; amount: number } };
  'savings.accounts.page': { payload: SavingsAccountsQuery; result: AppPage<AppSavingsAccount> };
  'savings.transactions.list': {
    payload: {
      savingsAccountId: string;
      page?: number;
      size?: number;
      from?: string;
      to?: string;
      sort?: 'ASC' | 'DESC';
    };
    result: AppPage<AppTransaction>;
  };
  'savings.requests.kyc': {
    payload: { requestId: string };
    result: {
      status: 'approved' | 'rejected' | 'awaiting-approval' | 'manual-review';
      sumsubAccessToken?: string;
    };
  };
  'savings.vrp.list': { payload: { savingsAccountId: string }; result: SavingsVrpMandate[] };
  'savings.vrp.cancel': {
    payload: { savingsAccountId: string; mandateId: string };
    result: SavingsVrpMandate;
  };
  'savings.nominated.accounts': { payload: { requestId: string }; result: SavingsNominatedAccountChoice[] };
  'savings.maturity.setAdvanced': {
    payload: { savingsAccountId: string; advanced: SavingsAdvancedMaturity };
    result: { savingsAccountId: string };
  };
  'savings.accounts.select': { payload: { savingsAccountId: string }; result: { selected: true } };
  'savings.create': { payload: SavingsAccountCreateRequest; result: SavingsAccountCreateResult };
  /**
   * The savings applications that have not become accounts yet — what a
   * KYC-gated `savings.create` leaves behind. Without it the pending application
   * was visible exactly once, in the response to the call that made it.
   *
   * `status` filters; omitting it returns every request the organisation has,
   * whatever its state. There is no paging: the platform route takes none.
   */
  'savings.requests.list': {
    payload: { status?: 'approved' | 'rejected' | 'awaiting-approval' | 'cancelled' };
    result: AppSavingsAccountRequest[];
  };
  /**
   * Renames a savings pot — the customer's own label, nothing else. The provider
   * write carries no usable body, so the result echoes what was set and a
   * resolved promise is the success signal.
   */
  'savings.accounts.rename': {
    payload: SavingsAccountRenameRequest;
    result: SavingsAccountRenameResult;
  };
  'savings.close': { payload: SavingsAccountCloseRequest; result: SavingsAccountCloseResult };
  /** Confirmation-gated where the project requires it (see docs on SCA). */
  'savings.internalTransfer': {
    payload: SavingsInternalTransferRequest;
    result: SavingsInternalTransferResult;
  };
  /**
   * Withdraws to the customer's nominated account. On a NOTICE account a
   * successful call has moved NOTHING — it lodged a withdrawal notice, and
   * the money leaves when the notice period ends. Read `result.outcome`
   * (see SavingsWithdrawResult for why the hosts derive it from the account
   * rather than the response body) and say different things for the two.
   *
   * A full-balance withdrawal is not a closure. The platform passes the
   * amount straight to the provider — no rejection, no auto-close, and the
   * project's `fullBalanceWithdrawalValidation` setting is read by nothing
   * on the backend. The *native* app diverts a full-balance withdrawal on a
   * closure-enabled account into its account-closure journey; a widget is
   * not forced down that path, so emptying a pot the customer means to close
   * is the widget's own call: use `savings.close` for the closure (some
   * providers — universal-sandbox for one — require exactly that sequence:
   * withdraw to zero, then close), or leave an emptied-but-open account,
   * which the platform accepts and the provider may not.
   *
   * Not confirmation-gated: the native flow runs no confirmation and the
   * route carries no ConfirmationGuard — like `savings.internalTransfer`,
   * the destination is the customer's own account.
   */
  'savings.withdraw': { payload: SavingsWithdrawRequest; result: SavingsWithdrawResult };
  /**
   * Records the maturity instruction for a fixed-term pot — applied at term
   * end, nothing moves now (see SavingsMaturitySetRequest for the three
   * decisions and the partial-reinvest arithmetic). Actionable while the
   * account's maturity window is open: gate on
   * `AppSavingsAccount.isRenewalEnabled`, because the platform itself
   * applies no window/account-type check — an out-of-window, non-fixed-term
   * or malformed decision is rejected by the *provider*, not before. One
   * provider caveat worth knowing at build time: thisbank accepts only the
   * two whole-balance decisions ('partial-reinvest' maps to an advanced
   * actions payload its adapter rejects) — the provider-support matrix
   * carries this per project.
   */
  'savings.maturity.set': { payload: SavingsMaturitySetRequest; result: SavingsMaturitySetResult };
  /**
   * The nominated account, or `null` when the customer has never linked one — a
   * state, not an error, and the one an application has to render before it can
   * usefully offer a withdrawal at all.
   */
  'savings.nominated.get': {
    payload: Record<string, never>;
    result: AppNominatedAccount | null;
  };
  /** The banks a nominated account may be linked from. `countryCode` defaults to 'GB'. */
  'savings.nominated.institutions': {
    payload: { countryCode?: string };
    result: AppNominatedAccountInstitution[];
  };
  /**
   * Starts an open-banking link. Nothing is nominated yet: the customer authorises
   * at `url`, returns to `redirectUrl`, and `savings.nominated.process` finishes it.
   */
  'savings.nominated.connect': {
    payload: {
      institutionId: string;
      /** Where the bank sends the customer back. Must be a URL this application serves. */
      redirectUrl: string;
      /** Carried to the bank as the consent reference. */
      reference?: string;
      userLanguage?: string;
    };
    result: NominatedAccountConnectResult;
  };
  /**
   * Finishes an open-banking link for `requestId`, and is also how a customer
   * chooses between the accounts a bank offered (`accountId`, after a
   * 'many-accounts' answer). Idempotent for a request already connected.
   *
   * On a project with a Confirmation of Payee provider the first call checks the
   * account: a full match (Accept) is linked at once and answers 'connected'; a
   * Refer answers 'cop-refer' instead of linking, and the second call sends that
   * result's `accountId` with the customer's `copDecision`. A
   * `copDecision` with no stored check, or for a different `accountId`, is refused.
   */
  'savings.nominated.process': {
    payload: { requestId: string; accountId?: string; copDecision?: NominatedAccountCopDecision };
    result: NominatedAccountProcessResult;
  };
  /**
   * The banks a PISP deposit can be authorised at — the payment provider's list, which is
   * not the same set as the account-linking (AISP) list above.
   */
  'savings.pisp.institutions': {
    payload: { countryCode?: string; currency?: string; feature?: string };
    result: AppNominatedAccountInstitution[];
  };
  /**
   * Remembers which bank authorises deposits from the nominated account, as the legacy
   * bank picker did (`POST /savings/pisp-set-institution`); `deposits.pisp` uses it, and an
   * application that finds `institutionId` already set can skip the picker.
   */
  'savings.pisp.setInstitution': {
    payload: { institutionId: string };
    result: AppNominatedAccount;
  };
  /**
   * Mints the billing request a custom payment page renders its compliance component for;
   * pass the id back as `billingRequestId` on the `deposits.pisp` hand-off.
   */
  'savings.pisp.prepare': { payload: PispDepositPrepareRequest; result: PispDepositPrepareResult };
  // There is no `savings.nominated.update`, and its absence is a decision (2026-08-27):
  // a nominated account is set only through the three operations above, and changing or
  // unsetting one is a bank-side support action. Do not re-add a hand-entry operation.
  /** The signed-in customer's stored picture, or null when absent. */
  'profile.picture.get': { payload: Record<string, never>; result: AppProfilePicture | null };
  /** The caller's active organisation; there is no id to pass, so no other organisation can be read. */
  'organisation.get': { payload: Record<string, never>; result: AppOrganisation };
  'profile.picture.set': { payload: AppProfilePictureUpload; result: AppProfilePicture };
  /**
   * Writes the signed-in customer's own details and answers the profile as it now stands, so
   * a form can re-render without a second read. The `profile` topic is the read half.
   *
   * Refusals worth handling separately: `verification-required` (an email or phone change
   * with no live OTP verification of the new value) and `forbidden` (the project does not
   * permit that field, or a contact-change cooldown is still running).
   */
  'profile.update': { payload: ProfileUpdateRequest; result: AppProfile };
  /**
   * The signed-in customer changing their own password. The current password is
   * re-checked before the new one is set, so this is not a reset — a customer
   * who cannot supply it belongs in the forgotten-password flow (`auth.flow.*`),
   * which is a different journey with a different proof of identity.
   *
   * Refusals are honest about which half failed: a wrong current password is
   * `forbidden`, a new password the realm's policy rejects is `invalid-payload`.
   * Neither payload field is ever logged.
   */
  'profile.changePassword': {
    payload: { currentPassword: string; newPassword: string };
    result: { changed: true };
  };
  /**
   * Two-step: quote, then accept `quoteId` before `expiresAt`. Re-quote by
   * calling this again; the previous quote is simply abandoned.
   */
  'exchange.rates': {
    payload: { base: string; symbols: string[] };
    result: { base: string; rates: Record<string, number> };
  };
  'exchange.refresh': { payload: ExchangeQuoteRequest & { quoteId: string }; result: ExchangeQuoteResult };
  'exchange.transactions.list': {
    payload: {
      accountId: string;
      page?: number;
      size?: number;
      from?: string;
      to?: string;
      type?: 'incoming' | 'outgoing';
      status?: string[];
      currency?: string;
      query?: string;
    };
    result: AppPage<ExchangeTransactionSummary>;
  };
  'exchange.transactions.get': { payload: { exchangeId: string }; result: ExchangeTransactionDetail };
  'exchange.quote': { payload: ExchangeQuoteRequest; result: ExchangeQuoteResult };
  'exchange.accept': { payload: { quoteId: string }; result: ExchangeAcceptResult };
  /** Cancels (reverses) a payment still open for cancellation. */
  'payments.scheduled.update': { payload: ScheduledPaymentUpdate; result: ScheduledPaymentUpdateResult };
  'payments.scheduled.cancel': { payload: { paymentId: string }; result: { paymentId: string } };
  'paymentRequests.create': {
    payload: PaymentRequestCreateRequest;
    result: PaymentRequestCreateResult;
  };
  /** Virtual cards only. */
  'cards.create': { payload: CardCreateRequest; result: CardCreateResult };
  'cards.orderOptions': { payload: { accountId: string }; result: CardOrderOptions };
  /**
   * Wallet push provisioning, step one: what the OS needs to start adding the card
   * (Apple: display data; Google: the OPC — and that is the whole hand-off). See contracts/cards.ts.
   */
  'cards.wallet.beginProvisioning': {
    payload: AppWalletBeginProvisioningRequest;
    result: AppWalletProvisioningPayload;
  };
  /** Wallet push provisioning, step two (Apple only): certificates + nonce → encrypted pass data. */
  'cards.wallet.completeProvisioning': {
    payload: AppWalletCompleteProvisioningRequest;
    result: AppWalletCompleteProvisioningResult;
  };
  /**
   * End-to-end encrypted card data (contracts/cards.ts, AppCardSecretsScheme). Use the
   * `react-app/card-secrets` helpers rather than these: they do the device's cryptography.
   * The one-time key for one reveal or write.
   */
  'cards.secureKey': { payload: { cardId: string }; result: AppCardSecureKey };
  /** PAN, CVV and expiry, encrypted under the cipher sent. Confirmation-gated (`getSensitiveCardDetails`) where SCA requires it. */
  'cards.secureDetails': {
    payload: { cardId: string; encryptedCipher: string };
    result: AppEncryptedCardDetails;
  };
  /** The PIN, encrypted under the cipher sent. Confirmation-gated (`getPin`) where SCA requires it. */
  'cards.pin.get': { payload: { cardId: string; encryptedCipher: string }; result: AppEncryptedCardPin };
  /** `encryptedPin`: the new PIN RSA-encrypted under a fresh `cards.secureKey`. */
  'cards.pin.set': { payload: { cardId: string; encryptedPin: string }; result: { cardId: string; updated: true } };
  /** `encryptedPassword`: the new 3DS password RSA-encrypted under a fresh `cards.secureKey`. */
  'cards.threeDsPassword.set': {
    payload: { cardId: string; encryptedPassword: string };
    result: { cardId: string; updated: true };
  };
  /** Keel Money only: whether ecommerce and non-domestic transactions are blocked on the card. */
  'keelmoney.cards.controls.get': { payload: { cardId: string }; result: KeelmoneyCardControls };
  /** Keel Money only: an omitted flag is left unchanged; at least one is required. Answers Keel's view after the change. */
  'keelmoney.cards.controls.set': {
    payload: { cardId: string; ecommerceBlocked?: boolean; nonDomesticBlocked?: boolean };
    result: KeelmoneyCardControls;
  };
  /**
   * Same wire endpoint (and same confirmation gate) as beneficiaries.create;
   * use this one when the destination is identified by IBAN/BIC or a
   * country-specific routing code. Confirmation-gated where the project
   * requires it.
   */
  'beneficiaries.createInternational': {
    payload: InternationalBeneficiaryCreateRequest;
    result: BeneficiaryCreateResult;
  };
  /**
   * Dry run of beneficiaries.createInternational: asks the provider whether it would accept these
   * details for their currency and route, and saves nothing. Checks format and completeness (ABA,
   * SWIFT, address…), not the account holder's name — that is beneficiaries.cop.check, which only
   * UK and SEPA accounts have. Not confirmation-gated; a rejection is `valid: false`, not an error.
   */
  'beneficiaries.validate': {
    payload: Omit<InternationalBeneficiaryCreateRequest, 'idempotencyKey'>;
    result: BeneficiaryValidateResult;
  };
  /**
   * Fee/total dry run for an international payment. Nothing moves, so this is
   * not confirmation-gated — call it before initiateInternational to show the
   * customer what leaves the account.
   */
  'payments.precheckInternational': {
    payload: InternationalPaymentRequest;
    result: InternationalPaymentPrecheckResult;
  };
  /** Confirmation-gated where the project requires it (see docs on SCA). */
  'payments.initiateInternational': {
    payload: InternationalPaymentRequest;
    result: InternationalPaymentResult;
  };
  /**
   * Moves money between two of the organisation's own accounts (current or
   * savings — the same account ids). The currency is the source account's; a
   * destination in another currency is refused. Confirmation-gated where the
   * project requires it (see docs on SCA). Served by the Application BFF only.
   */
  'payments.internalTransfer': {
    payload: InternalTransferRequest;
    result: InternalTransferResult;
  };
  /**
   * Host-mediated download: resolves once the browser's save flow has started
   * and never returns bytes or a URL (see AppDocumentDownloadResult).
   */
  'documents.download': {
    payload: AppDocumentRequest;
    result: AppDocumentDownloadResult;
  };
  /**
   * The documents the platform already holds for one context — an account's
   * statements, a transaction's receipt, the customer's own file. Not a
   * generated statement: those are `documents.download` with a date range and
   * have no listing.
   *
   * `entityId` names the thing the documents hang off (the savings account id,
   * the current-account id, the transaction id) and defaults to the signed-in
   * customer for `context: 'customer'`. Omitting `category` lists every
   * category in that context.
   */
  'documents.list': {
    payload: { context: AppDocumentContext; entityId?: string; category?: AppDocumentCategory };
    result: AppStoredDocument[];
  };
  /**
   * Adds one document to the customer's store. The bytes travel base64 on the
   * payload; hosts cap the decoded size and refuse file types outside the
   * platform's upload allowlist, and both refusals are `invalid-payload`.
   *
   * Answers only the new document's id: the row's other fields are the store's
   * to decide, and a widget that needs them re-reads `documents.list`.
   */
  'documents.upload': {
    payload: AppDocumentUploadRequest;
    result: { documentId: string };
  };
  /**
   * Removes one document from the customer's store, by the id
   * `documents.list` returned. Deleting a document that is already gone answers
   * `deleted: false` rather than failing.
   */
  'documents.delete': {
    payload: { documentId: string; context: AppDocumentContext; entityId?: string };
    result: { deleted: boolean };
  };
  /**
   * The customer's in-app notification feed, newest first. Paged, because a
   * feed is a collection — see the note on AppDataTopics for why it is not a
   * topic. `notifications.received` is the live channel for what arrives while
   * the page is open.
   */
  'notifications.preferences.get': { payload: Record<string, never>; result: AppNotificationPreferences };
  /** Patch only the supplied categories/channels; omitted preferences are unchanged. */
  'notifications.preferences.update': {
    payload: { preferences: AppNotificationPreferences };
    result: AppNotificationPreferences;
  };
  /** Notification Policy: groups and types the customer can switch, and their check-in level. */
  'notifications.settings.get': { payload: Record<string, never>; result: AppNotificationSettings };
  /** Unknown keys → `unknown-group`/`unknown-type`; a locked group off → `locked-group`; policy off → 409. */
  'notifications.settings.update': {
    payload: AppNotificationSettingsPatch;
    result: AppNotificationSettingsUpdateResult;
  };
  /** `read` filters the page (`undefined` = the whole feed). See AppNotificationsPage. */
  'notifications.list': {
    payload: { page?: number; size?: number; read?: boolean };
    result: AppNotificationsPage;
  };
  /** Marks the listed messages read, or the whole feed when `ids` is omitted. */
  'notifications.markRead': {
    payload: { ids?: string[] };
    result: AppNotificationsMarkReadResult;
  };
  /**
   * Push: binds this installation's FCM token to the customer's notification subscriber.
   * See contracts/notifications.ts. Idempotent per token.
   */
  'notifications.registerDevice': {
    payload: AppPushRegisterRequest;
    result: AppPushRegisterResult;
  };
  /** Push: drops one token — called before logout. Unknown token is a quiet success. */
  'notifications.unregisterDevice': {
    payload: AppPushUnregisterRequest;
    result: AppPushUnregisterResult;
  };
  /**
   * Binds a device key to the signed-in customer, so the realm's login flow can later
   * challenge it instead of asking for a password again. The private half never leaves the
   * device; this enrols the public half. See contracts/device.ts for the defaults.
   *
   * Always confirmed (SCA), whatever the project's confirmation settings say.
   */
  'device.enroll': {
    payload: AppDeviceEnrolRequest;
    result: AppDeviceEnrolResult;
  };
  /**
   * The customer's enrolled devices, for a management screen. Never includes a public key.
   */
  'device.list': {
    payload: Record<string, never>;
    result: AppDeviceListResult;
  };
  /**
   * Drops one enrolled device, by the `deviceId` `device.list` published. Removing a device
   * that is not enrolled is `invalid-payload`, not a quiet success.
   *
   * Always confirmed (SCA), for the same reason `device.enroll` is.
   */
  'device.remove': {
    payload: { deviceId: string; confirmationRef?: string };
    result: AppDeviceRemoveResult;
  };
  /**
   * Issues this device's biometric credential: a Keycloak offline token for the signed-in
   * customer, returned once, for the biometric vault only (see contracts/device.ts). Device
   * (bearer) sessions only — a browser session answers 403 `bearer-session-required`. Calling it
   * again issues a second credential; revoke the first.
   */
  'session.biometric.enroll': {
    payload: Record<string, never>;
    result: AppBiometricCredential;
  };
  /**
   * Ends a biometric credential at Keycloak, by the `credentialId` enroll returned. Only the
   * signed-in customer's own; anything else answers `revoked: false`. Signing out ends the
   * credential linked to that session as well.
   */
  'session.biometric.revoke': {
    payload: { credentialId: string };
    result: AppBiometricRevokeResult;
  };
  /** Uploads a UTF-8 CSV (maximum 512 KiB) and starts asynchronous native validation/payee checks. Poll get; this does not execute payments. */
  'bulkPayments.create': {
    payload: {
      accountId: string;
      fileName: string;
      contentBase64: string;
      showInBeneficiaryList: boolean;
      confirmationRef?: string;
    };
    result: { bulkPaymentId: string };
  };
  /** Creates the reviewed batch beneficiaries. Poll get for processing and validation results. */
  'bulkPayments.addBeneficiaries': {
    payload: { bulkPaymentId: string; showInBeneficiaryList: boolean; confirmationRef?: string };
    result: { bulkPaymentId: string };
  };
  /** Removes a transfer from a batch before beneficiary creation or execution. */
  'bulkPayments.removeTransfer': {
    payload: { bulkPaymentId: string; transferId: string };
    result: { bulkPaymentId: string; transferId: string };
  };
  /** Accepts the native payee-check suggested name (or rejects it). Never treats an unchecked payee as a match. */
  'bulkPayments.updatePayee': {
    payload: { bulkPaymentId: string; transferId: string; suggestion: boolean; name?: string };
    result: { bulkPaymentId: string; transferId: string };
  };
  /** Requests native bulk-payment approval. This does not approve or execute the payment. */
  'bulkPayments.requestApproval': {
    payload: { bulkPaymentId: string; confirmationRef?: string };
    result: { bulkPaymentId: string };
  };
  /** Starts native batch execution for an authorised initiator. Approval-required users must use the native approval flow. Poll get for outcomes. */
  'bulkPayments.execute': {
    payload: { bulkPaymentId: string; confirmationRef?: string };
    result: { bulkPaymentId: string };
  };
  /** Cancels the remaining cancellable rows using the native service; already sent payments cannot be recalled. */
  'bulkPayments.cancel': {
    payload: { bulkPaymentId: string; confirmationRef?: string };
    result: { bulkPaymentId: string };
  };
  /** Reschedules a pending scheduled row before batch approval/execution. Date only (YYYY-MM-DD); the daily scheduler determines execution time. */
  'bulkPayments.rescheduleTransfer': {
    payload: { bulkPaymentId: string; transferId: string; scheduleDate: string; confirmationRef?: string };
    result: { bulkPaymentId: string; transferId: string };
  };
  /** Cancels one pending scheduled row. Already sent payments cannot be recalled. */
  'bulkPayments.cancelScheduledTransfer': {
    payload: { bulkPaymentId: string; transferId: string; confirmationRef?: string };
    result: { bulkPaymentId: string; transferId: string };
  };
  /** Uploaded bulk-payment batches, newest first. `query` searches the batch. */
  'bulkPayments.list': {
    payload: { page?: number; size?: number; query?: string };
    result: AppPage<AppBulkPayment>;
  };
  /** One batch: a page of its transfers plus the batch totals. */
  'bulkPayments.get': {
    payload: { bulkPaymentId: string; page?: number; size?: number; query?: string };
    result: AppBulkPaymentDetail;
  };
  /**
   * Re-attempts one failed transfer inside a batch. Both ids are required: the
   * batch and the row. The endpoint answers with no usable body, so the result
   * echoes what was retried — re-read bulkPayments.get for the new status.
   *
   * Upload and preparation are available through bulkPayments.create and the
   * batch commands. Retry still requires an Idempotency-Key header on the BFF.
   */
  'bulkPayments.retry': {
    payload: { bulkPaymentId: string; transferId: string };
    result: { bulkPaymentId: string; transferId: string };
  };
  /** Rails available for a destination; both filters are optional. */
  'payments.schemes.list': {
    payload: { country?: string; currency?: string };
    result: AppPaymentScheme[];
  };
  /**
   * The onboarding flow as the host sees it: which steps are done, available or
   * blocked, and whether a submit would pass right now.
   *
   * Pass the contact values you intend to submit. Verification is bound to the
   * *value* server-side, so without them the answer can only be "some
   * verification exists", which is not the question a submit button is asking.
   * `kind` defaults to 'individual'.
   *
   * Read-only and side-effect free: call it on mount, after each verification,
   * and before enabling a submit.
   */
  'onboarding.identity.create': { payload: AppOnboardingIdentityCreateRequest; result: AppOnboardingIdentityCreateResult };
  'onboarding.answers.save': { payload: { answers: Record<string, string>; consent?: { termsVersion: string; privacyVersion: string } }; result: AppOnboardingJourney };
  'onboarding.journey.get': { payload: Record<string, never>; result: AppOnboardingJourney };
  'onboarding.flow': {
    payload: { kind?: AppOnboardingKind; email?: string; phone?: string };
    result: AppOnboardingFlow;
  };
  /**
   * Which fields this project requires. See AppOnboardingRequirements for why
   * `extraFieldsKnown: false` is a real answer and not a failure.
   */
  /** Safe, versioned drafts for the new application only. The ID is a bearer resume capability. */
  'onboarding.progress.save': {
    payload: {
      formSessionId?: string;
      source: 'web' | 'mobile';
      step: string;
      data: Record<string, unknown>;
    };
    result: { formSessionId: string; expiresAt: string };
  };
  'onboarding.progress.load': {
    payload: { formSessionId: string };
    result: {
      formSessionId: string;
      expiresAt: string;
      source: 'web' | 'mobile';
      step: string;
      data: Record<string, unknown>;
    };
  };
  'onboarding.progress.complete': {
    payload: { formSessionId: string };
    result: { completed: true };
  };
  'onboarding.customerMatch': {
    payload: CustomerMatchRequest;
    result: CustomerMatchResult;
  };
  'onboarding.requirements': {
    payload: { kind?: AppOnboardingKind };
    result: AppOnboardingRequirements;
  };
  /** Checks a nominated account; the single-use proof remains on the server. */
  'onboarding.bank.verify': {
    payload: { name: string; sortCode: string; accountNumber: string };
    result: { name: string; matchStatus: boolean; matchStatusCode: string; expiresAt: string };
  };
  /**
   * Sends an OTP to an email address or phone number.
   *
   * Everything that makes the send work — the signed nonce fetched per request,
   * the bot-check token, the provider — is host-side and opaque here. What
   * crosses back is the code length and the resend delay.
   *
   * Rejects with 'rate-limited' (details: AppRateLimitedDetails) when the
   * platform's per-IP limit is hit: honour `retryAfterSeconds`, do not retry on
   * a timer of your own.
   */
  'onboarding.verify.send': {
    payload: AppVerificationSendRequest;
    result: AppVerificationSendResult;
  };
  /**
   * Confirms an OTP. Resolves only on success (a wrong or expired code rejects),
   * and carries the expiry: verification lapses, and a widget that shows a
   * countdown is a widget whose customers do not hit a 403 at submit.
   */
  'onboarding.verify.confirm': {
    payload: AppVerificationConfirmRequest;
    result: AppVerificationConfirmResult;
  };
  /**
   * Submits an individual onboarding application.
   *
   * The host checks the prerequisites **before any HTTP call** and rejects with
   * 'onboarding-prerequisite-unmet' (details: AppPrerequisiteUnmetDetails)
   * naming the outstanding steps — so a widget cannot produce a server 403 by
   * accident. Both channels must be verified, unexpired, and verified *for the
   * email and phone in this payload*.
   *
   * `status` in the result is usually not terminal; watch `onboardingStatus`.
   */
  'onboarding.submitIndividual': {
    payload: OnboardIndividualRequest;
    result: OnboardIndividualResult;
  };
  /**
   * Submits a company onboarding application (company + members, with the
   * primary member becoming the platform user).
   *
   * Same prerequisite gate as the individual submit, applied to the **primary
   * member's** email and phone — and enforced by the server too, which requires
   * the single-use verification tokens on the submit. The host presents them
   * itself (they are host-held and never cross the port); the gate here just
   * turns the server's 403 into a named, pre-flight rejection.
   *
   * `status` in the result is the KYB status, usually not terminal; watch
   * `onboardingStatus`. No `extra` bag — see OnboardBusinessRequest.
   */
  'onboarding.submitBusiness': {
    payload: OnboardBusinessRequest;
    result: OnboardBusinessResult;
  };
  /**
   * Launches identity verification in a **host-owned** overlay and resolves
   * `{ launched: true }`. Never returns the provider token or the session — see
   * AppVerifyIdentityResult. Requires a completed submit; otherwise rejects
   * with 'onboarding-prerequisite-unmet'.
   */
  'onboarding.verifyIdentity': {
    payload: AppVerifyIdentityRequest;
    result: AppVerifyIdentityResult;
  };
  /**
   * Starts identity verification with the project's KYC provider and returns what the
   * application needs to launch it — see IdentityVerificationStartResult. Application-only (BFF);
   * never offered to a widget. Veriff can start before onboarding submission for a signed-in
   * identity. Use identity.verification.profile for the retained decision and extracted fields.
   */
  'identity.verification.start': {
    payload: Record<string, never>;
    result: IdentityVerificationStartResult;
  };
  /** Reads the signed-in user's retained Veriff identity. No document fields before approval. */
  'identity.verification.profile': {
    payload: Record<string, never>;
    result: IdentityVerificationProfile;
  };
  /** The project's raw Keycloak password-policy string; available before sign-in. */
  'auth.passwordPolicy': { payload: Record<string, never>; result: { policy: string } };
  /**
   * Whether there is a signed-in customer, and who. Read-only, side-effect free
   * and answers on an anonymous onboarding page as readily as on a dashboard —
   * call it on mount rather than inferring a session from a failed topic read.
   *
   * Still an ordinary grant: it is a *session* fact, and the platform has no
   * implicitly-granted api entry (every read-only operation, `onboarding.flow`
   * included, has to be listed).
   */
  'auth.status': { payload: Record<string, never>; result: AppAuthStatus };
  /**
   * Sends the customer to the project's sign-in flow, optionally naming the page
   * to return to. Resolves `{ navigated: true }` once the host has begun it —
   * this document is likely replaced, so paint first and expect no later turn.
   */
  'auth.login': { payload: AppAuthLoginRequest; result: AppAuthLoginResult };
  /**
   * Performs the platform's real logout: the host revokes the session
   * server-side, clears the stored one and redirects. A widget cannot clear a
   * session itself — it holds none.
   */
  'auth.logout': { payload: Record<string, never>; result: AppAuthLogoutResult };
  /**
   * Begins a managed login flow and answers with its first step.
   *
   * The host does everything that involves a credential: it makes the
   * authorization request, keeps the Keycloak cookies and the opaque
   * continuation URL, and hands back only the sanitised step. `loginHint`
   * pre-fills the identifier the flow will ask for (an email or username the
   * page already knows) — it is a hint, not an assertion, and the flow still
   * asks for whatever its first step declares.
   *
   * Calling it again discards the transaction in progress and starts a fresh
   * one; there is no way to resume an abandoned flow. Anonymous-safe, like the
   * rest of `auth.*` — it is what runs *before* there is a session.
   *
   * Rejects when the project has no headless login flow configured. Standalone
   * throttling rejects with rate-limited and sanitised retryAfterSeconds details.
   */
  'auth.flow.start': { payload: { loginHint?: string }; result: AppAuthFlowState };
  /**
   * Answers the current step, or asks for one of the alternatives it offers.
   *
   * `values` are keyed by `AppAuthFlowField.name`, exactly as the step
   * declared them. `action` picks one of `AppAuthFlowStep.actions`
   * ('forgot_password', 'resend_code', 'restart_flow', …) — the host posts it to
   * the continuation URL it holds; an action the current step does not list is
   * rejected rather than attempted.
   *
   * **A wrong password or a bad code is not a rejection.** It resolves with the
   * next `AppAuthFlowStep`, usually the same one, carrying `error` — because
   * the flow is still alive and the widget's job is to re-render it. Rejections
   * are reserved for the flow being unusable: no transaction in progress, an
   * expired or discarded one, a malformed payload, a transport failure.
   *
   * When the flow finishes the result is `AppAuthFlowComplete` and the host
   * has already signed the customer in; with `redirected: true` this frame may
   * be replaced at any moment.
   */
  'auth.flow.submit': {
    payload: { values: Record<string, string | string[]>; action?: string };
    result: AppAuthFlowState;
  };
  /**
   * Switches to another authentication method the current step offered in
   * `methods` ("try another way"), and answers with the step that choice leads
   * to.
   *
   * `methodId` is the opaque execution id from `AppAuthFlowMethod.id`, passed
   * back unchanged. The server validates it against the current selection set,
   * so an id from an earlier step — or one a widget assembled — is rejected, not
   * honoured.
   */
  'auth.flow.selectMethod': { payload: { methodId: string }; result: AppAuthFlowState };
  /**
   * Abandons the flow. The host discards the login transaction, its cookie jar
   * and its continuation URL; nothing is left to resume, and a later `submit`
   * without a fresh `start` rejects.
   *
   * `cancelled` is false when there was no flow in progress — cancelling twice
   * is not an error, it is a no-op that says so.
   */
  'auth.flow.cancel': { payload: Record<string, never>; result: { cancelled: boolean } };
  /**
   * Provider-specific capabilities the current project's BaaS exposes.
   * Namespaced so a later provider can add `rfi.*`, `onboarding.delegated.*`,
   * etc. without growing a new first-class operation per vendor.
   */
  'providerCapabilities.list': {
    payload: Record<string, never>;
    result: {
      provider: string;
      capabilities: Array<{ name: string; description: string; version: number }>;
    };
  };
  /**
   * Invoke one capability from `providerCapabilities.list`.
   * The host resolves the active provider and rejects unknown names.
   */
  'providerCapabilities.invoke': {
    payload: { capability: string; payload?: Record<string, unknown> };
    result: unknown;
  };

  /* ── Data Studio records ───────────────────────────────────────────────────
   *
   * The operation form of the same durable documents `sdk.storage.*` serves an
   * in-app widget (protocol.ts DataDefinition / DataDocument are literally the
   * shapes below). A code application reaches the platform over one transport —
   * `operations/*` and `data/*` — so records arrive as operations rather than as
   * a host API, and the grant is the operation itself: `capabilities.operations`
   * in `finxone.app.json` lists the names, the application token carries them as
   * `app:op:records.*`, and read is separated from write by which of those five
   * names was granted. There is no `capabilities.records`.
   *
   * **A record is one document per definition per subject**, not a collection:
   * the definition's own `scope` picks the subject (`user` → the signed-in
   * person, `organisation` → their organisation), and each subject has at most
   * one. So `records.list` lists the *definitions* the application can reach and
   * the other four act on the single record of one definition — but only for a
   * definition whose `shape` is 'document'. A `shape: 'collection'` definition
   * holds many rows and is reached by the `records.items.*` five below; calling
   * these five on one is refused as 'wrong-shape'.
   *
   * `definition` is the Data Studio slug (`expense-claim`); the `data/<slug>`
   * reference form `records.list` returns is accepted too, so a listing can be
   * fed straight back in.
   */

  /** Definitions this application may reach, with the schema each record must satisfy. */
  'records.list': { payload: Record<string, never>; result: DataDefinition[] };
  /** This caller's record for one definition, or null when there is none yet. */
  'records.get': { payload: { definition: string }; result: DataDocument | null };
  /** Writes the first revision; rejects `conflict` when a record already exists. */
  'records.create': {
    payload: { definition: string; value: Record<string, unknown> };
    result: DataDocument;
  };
  /**
   * Replaces the record. `ifRevision` is the optimistic guard — pass the revision
   * `records.get` answered with and a concurrent write rejects `conflict` instead
   * of being overwritten. Omitting it replaces whatever is there.
   */
  'records.update': {
    payload: { definition: string; value: Record<string, unknown>; ifRevision?: number };
    result: DataDocument;
  };
  /**
   * Removes the record. `deleted` is false when there was nothing to remove —
   * deleting twice is a no-op that says so, not an error.
   */
  'records.delete': {
    payload: { definition: string; ifRevision?: number };
    result: { definition: string; deleted: boolean };
  };

  /* ── Data Studio records: collections ──────────────────────────────────────
   *
   * The other shape a definition can have. A `shape: 'collection'` definition
   * holds many rows per subject, each with its own `recordId`, and these five
   * are the only operations that reach them — the document five above answer
   * 'wrong-shape' on a collection, and these answer 'wrong-shape' on a document.
   *
   * `definition` is the same slug the document five take (`data/<slug>` accepted
   * too). Granting is unchanged: five more names in `capabilities.operations`,
   * carried as `app:op:records.items.*`, with read separated from write by which
   * names were granted.
   *
   * A query is answered from declared indexes, never a scan, which is why it can
   * be refused for reasons a document read never has: see
   * DATA_RECORD_ERROR_REASONS (protocol.ts) for the whole list and the
   * vocabulary code each is reported as.
   */

  /**
   * A page of one collection. `where` clauses are ANDed; `orderBy` is applied in
   * the order given; `limit` defaults to DATA_RECORD_QUERY_DEFAULT_LIMIT and is
   * capped at DATA_RECORD_QUERY_MAX_LIMIT.
   *
   * `cursor` is the opaque `nextCursor` of the previous page — pass it back
   * untouched, with the same `where`/`orderBy`, and never construct one.
   */
  'records.items.query': {
    payload: {
      definition: string;
      where?: RecordFilter[];
      orderBy?: RecordSort[];
      limit?: number;
      cursor?: string | null;
    };
    result: RecordPage;
  };
  /** One row by id, or null when the collection holds no such row. */
  'records.items.get': { payload: { definition: string; recordId: string }; result: DataRecord | null };
  /**
   * Appends a row. `idempotencyKey` is the duplicate guard for a create a user
   * can double-submit: the same key replays the first row rather than writing a
   * second, and a different key always writes.
   */
  'records.items.create': {
    payload: { definition: string; value: Record<string, unknown>; idempotencyKey?: string };
    result: DataRecord;
  };
  /**
   * Replaces one row's value. `ifRevision` is the optimistic guard — pass the
   * revision the read answered with and a concurrent write rejects `conflict`
   * ('invalid-revision') instead of being overwritten.
   */
  'records.items.update': {
    payload: {
      definition: string;
      recordId: string;
      value: Record<string, unknown>;
      ifRevision?: number;
    };
    result: DataRecord;
  };
  /**
   * Removes one row. `deleted` is false when there was nothing to remove —
   * deleting twice is a no-op that says so, not an error.
   */
  'records.items.delete': {
    payload: { definition: string; recordId: string; ifRevision?: number };
    result: { recordId: string; deleted: boolean };
  };
}
